
/* Desktop bulk modal: stage quantities locally, then add all selected variants. */
(() => {
  if (window.desktopBulkActionsInitialized) return;
  window.desktopBulkActionsInitialized = true;

  const desktop = () => window.matchMedia('(min-width: 990px)').matches;
  const pending = new WeakSet();
  const controls = '[data-desktop-bulk-controls]';
  const quantitySelector = '[data-desktop-bulk-quantity]';
  const root = () => window.Shopify?.routes?.root || '/';

  // Align controls to variant text, including image slots.
  let alignmentQueued = false;
  const observedLists = new WeakSet();

  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(queueAlignment)
      : null;

  function alignLists() {
    alignmentQueued = false;
    if (!desktop()) return;

    for (const list of document.querySelectorAll('.desktop-bulk-list')) {
      if (!observedLists.has(list)) {
        resizeObserver?.observe(list);
        observedLists.add(list);
      }

      const counter = list.querySelector(
        '.desktop-bulk-controls .quantity'
      );

      if (counter) {
        const counterSize = counter.getBoundingClientRect();

        if (counterSize.width > 0 && counterSize.height > 0) {
          list.style.setProperty(
            '--desktop-counter-width',
            `${counterSize.width}px`
          );

          list.style.setProperty(
            '--desktop-counter-height',
            `${counterSize.height}px`
          );
        }
      }

      const title = list.querySelector(
        '.variant-item__inner .variant-item__name'
      );

      const heading = list.querySelector(
        '.quick-order-list__table thead th:first-child'
      );

      const footer = list.querySelector(
        '.quick-order-list-total__column:first-child .quick-order-list-buttons'
      );

      if (
        !title ||
        !heading ||
        title.getBoundingClientRect().width === 0
      ) {
        continue;
      }

      const textLeft = title.getBoundingClientRect().left;

      list.style.setProperty(
        '--desktop-variant-heading-indent',
        `${Math.max(
          0,
          textLeft - heading.getBoundingClientRect().left
        )}px`
      );

      if (footer) {
        list.style.setProperty(
          '--desktop-variant-footer-indent',
          `${Math.max(
            0,
            textLeft - footer.getBoundingClientRect().left
          )}px`
        );
      }
    }
  }

  function queueAlignment() {
    if (alignmentQueued) return;
    alignmentQueued = true;

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(alignLists);
    } else {
      alignLists();
    }
  }

  function watchAlignment() {
    if (
      typeof MutationObserver !== 'undefined' &&
      document.body
    ) {
      new MutationObserver(queueAlignment).observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    queueAlignment();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      watchAlignment,
      { once: true }
    );
  } else {
    watchAlignment();
  }

  window.addEventListener('resize', queueAlignment);
  document.fonts?.ready.then(queueAlignment);

  // Prevent Shopify's automatic quantity-change handler from
  // interfering with locally staged desktop quantities.
  document.addEventListener(
    'change',
    (event) => {
      if (desktop() && event.target.closest(controls)) {
        event.stopPropagation();
      }
    },
    true
  );

  // Submit staged quantities when Enter is pressed.
  document.addEventListener(
    'keydown',
    (event) => {
      if (
        !desktop() ||
        !event.target.matches(quantitySelector) ||
        event.key !== 'Enter'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      event.target
        .closest('quick-order-list')
        ?.querySelector('[data-desktop-bulk-add]')
        ?.click();
    },
    true
  );

  // Update only the cart quantity badge.
  // Never replace the shopping bag SVG or header link.
  function updateCartBadge(cartData) {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) return;

    const count = Number(cartData?.item_count) || 0;
    let bubble = cartIcon.querySelector('.cart-count-bubble');

    if (count === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      cartIcon.appendChild(bubble);
    }

    bubble.classList.remove('hidden');
    bubble.innerHTML = '';

    const visibleCount = document.createElement('span');
    visibleCount.textContent = String(count);

    const accessibleCount = document.createElement('span');
    accessibleCount.className = 'visually-hidden';
    accessibleCount.textContent = `${count} items in cart`;

    bubble.appendChild(visibleCount);
    bubble.appendChild(accessibleCount);
  }

  // Refresh cart drawer contents without changing the header.
  function updateCartDrawer(data) {
    const drawer = document.querySelector('cart-drawer');
    const html = data?.sections?.['cart-drawer'];

    if (!drawer || !html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    const replacement = doc.querySelector('.drawer__inner');
    const current = drawer.querySelector('.drawer__inner');

    if (!replacement || !current) return;

    current.innerHTML = replacement.innerHTML;
    drawer.classList.remove('is-empty');
  }

  // Handle desktop popup quantity and Add To Cart actions.
  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest(
        '[data-desktop-bulk-add], [data-desktop-bulk-step]'
      );

      if (!button || !desktop()) return;

      const list = button.closest('quick-order-list');

      if (!list?.classList.contains('desktop-bulk-list')) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (pending.has(list)) return;

      const message = list.querySelector(
        '[data-desktop-bulk-message]'
      );

      if (message) message.textContent = '';

      // Increment/decrement buttons.
      if (button.hasAttribute('data-desktop-bulk-step')) {
        const input = button
          .closest(controls)
          ?.querySelector(quantitySelector);

        if (!input) return;

        const min = Number(input.dataset.min);
        const step = Number(input.dataset.step);
        const max =
          input.dataset.max === ''
            ? Infinity
            : Number(input.dataset.max);

        const value = Number(input.value);
        const direction = Number(button.dataset.desktopBulkStep);

        let next =
          (Number.isFinite(value) ? value : 0) +
          direction * step;

        if (direction > 0 && value === 0) {
          next = min;
        }

        if (direction < 0 && next < min) {
          next = 0;
        }

        input.value = String(
          Math.max(0, Math.min(max, next))
        );

        return;
      }

      // Collect locally selected variant quantities.
      const inputs = Array.from(
        list.querySelectorAll(quantitySelector)
      );

      const items = [];

      for (const input of inputs) {
        const value = Number(input.value);
        const min = Number(input.dataset.min);
        const step = Number(input.dataset.step);

        const max =
          input.dataset.max === ''
            ? Infinity
            : Number(input.dataset.max);

        const invalid =
          !/^\d+$/.test(input.value.trim()) ||
          !Number.isSafeInteger(value) ||
          value < 0 ||
          (
            value !== 0 &&
            (
              value < min ||
              value > max ||
              value % step !== 0
            )
          );

        if (invalid) {
          if (message) {
            message.textContent =
              `Use zero or a quantity of at least ${min}, ` +
              `in multiples of ${step}` +
              (
                max === Infinity
                  ? '.'
                  : `, up to ${max}.`
              );
          }

          input.focus();
          return;
        }

        if (value === 0) continue;

        const item = {
          id: input.closest(controls).dataset.variantId,
          quantity: value
        };

        if (input.dataset.sellingPlan) {
          item.selling_plan = input.dataset.sellingPlan;
        }

        items.push(item);
      }

      if (!items.length) {
        if (message) {
          message.textContent =
            'Choose at least one quantity to add.';
        }
        return;
      }

      // Avoid conflicting with native quantity updates.
      if (
        list.hasPendingQuantityUpdate ||
        list.queue?.length > 0
      ) {
        if (message) {
          message.textContent =
            'Please wait for your cart update to finish, then try again.';
        }
        return;
      }

      pending.add(list);

      const buttons = Array.from(
        list.querySelectorAll(
          '[data-desktop-bulk-add], [data-desktop-bulk-step]'
        )
      );

      buttons.forEach(element => {
        element.disabled = true;
      });

      inputs.forEach(input => {
        input.disabled = true;
      });

      button.setAttribute('aria-busy', 'true');

      let added = false;

      try {
        // Add all selected variants in one Shopify request.
        const response = await fetch(
          root() + 'cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items,
              sections: [
                'cart-icon-bubble',
                'cart-drawer'
              ],
              sections_url:
                window.location.pathname +
                window.location.search
            })
          }
        );

        const data = await response.json();

        if (!response.ok || data.status >= 400) {
          throw new Error(
            data.description ||
            data.message ||
            'Could not add these items.'
          );
        }

        added = true;

        // Clear staged quantities to prevent accidental re-adding.
        inputs.forEach(input => {
          input.value = '0';
        });

        // Refresh the drawer contents.
        updateCartDrawer(data);

        // Fetch the authoritative cart quantity.
        const cartResponse = await fetch(
          root() + 'cart.js',
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );

        if (!cartResponse.ok) {
          throw new Error(
            'Could not refresh cart information.'
          );
        }

        const cartData = await cartResponse.json();

        // Update only the number badge.
        updateCartBadge(cartData);

        // Let other Shopify components know the cart changed.
        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: list.id,
            cartData
          });
        }

        // Refresh popup quantities and totals.
        if (typeof list.refresh === 'function') {
          await list.refresh();
        }

        const updatedMessage =
          list.querySelector('[data-desktop-bulk-message]') ||
          message;

        if (updatedMessage) {
          updatedMessage.textContent = 'Added to cart.';
        }

        list.querySelector('[data-desktop-bulk-add]')?.focus({
          preventScroll: true
        });

      } catch (error) {
        if (message) {
          message.textContent = added
            ? 'Added to cart. Reload the page to see updated totals.'
            : (
                error.message ||
                'Could not add these items. Please try again.'
              );
        }

        if (!added) {
          console.error('Desktop bulk add failed:', error);
        }

      } finally {
        pending.delete(list);

        buttons.forEach(element => {
          if (element.isConnected) element.disabled = false;
        });

        inputs.forEach(input => {
          if (input.isConnected) input.disabled = false;
        });

        button.removeAttribute('aria-busy');

        queueAlignment();
      }
    },
    true
  );
})();
